
import React, { useEffect, useRef } from 'react';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { bbox, squareGrid } from '@turf/turf'; // Import specific functions from main turf package
import booleanIntersects from '@turf/boolean-intersects'; // Import booleanIntersects from its specific package

interface MapProps {
  stateGeoJson: any;
  cityGeoJson: any;
  zoning: number; // Add this prop
}

const Map: React.FC<MapProps> = ({ stateGeoJson, cityGeoJson, zoning }) => {
  const mapRef = useRef<L.Map | null>(null);
  const osmLayerRef = useRef<L.TileLayer | null>(null);
  const googleHybridLayerRef = useRef<L.TileLayer | null>(null);
  const [mapType, setMapType] = React.useState<'osm' | 'googleHybrid'>('osm');
  const stateLayerRef = useRef<L.GeoJSON | null>(null);
  const cityLayerRef = useRef<L.GeoJSON | null>(null);
  const gridLayerRef = useRef<L.GeoJSON | null>(null); // New ref for the grid

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = L.map('map').setView([-15.78347, -47.8825], 4);

      osmLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      });

      googleHybridLayerRef.current = L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
        maxZoom: 20,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        attribution: '&copy; Google',
      });

      osmLayerRef.current.addTo(mapRef.current);

      // Add map type toggle control
      const mapTypeControl = (L.control as any)({ position: 'topright' });
      mapTypeControl.onAdd = () => {
        const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        div.innerHTML = `
          <select id="mapTypeSelect" style="width: 100px; padding: 5px;">
            <option value="osm">OpenStreetMap</option>
            <option value="googleHybrid">Google Hybrid</option>
          </select>
        `;
        L.DomEvent.disableClickPropagation(div);
        return div;
      };
      mapTypeControl.addTo(mapRef.current);

      mapRef.current.on('layeradd layerremove', () => {
        const select = document.getElementById('mapTypeSelect') as HTMLSelectElement;
        if (select) {
          select.value = mapType;
          select.onchange = (e) => {
            setMapType((e.target as HTMLSelectElement).value as 'osm' | 'googleHybrid');
          };
        }
      });
    }
  }, []);

  // Effect to switch map layers
  useEffect(() => {
    if (!mapRef.current || !osmLayerRef.current || !googleHybridLayerRef.current) return;

    if (mapType === 'osm') {
      mapRef.current.removeLayer(googleHybridLayerRef.current);
      mapRef.current.addLayer(osmLayerRef.current);
    } else {
      mapRef.current.removeLayer(osmLayerRef.current);
      mapRef.current.addLayer(googleHybridLayerRef.current);
    }
  }, [mapType]);

  // Handle state layer
  useEffect(() => {
    if (stateLayerRef.current) {
      mapRef.current?.removeLayer(stateLayerRef.current);
    }
    // Clear city and grid layers when state changes
    if (cityLayerRef.current) {
      mapRef.current?.removeLayer(cityLayerRef.current);
      cityLayerRef.current = null;
    }
    if (gridLayerRef.current) {
      mapRef.current?.removeLayer(gridLayerRef.current);
      gridLayerRef.current = null;
    }

    if (stateGeoJson && mapRef.current) {
      const stateLayer = L.geoJSON(stateGeoJson, {
        style: {
          fillOpacity: 0,
          color: '#3388ff', // Default blue
        },
      }).addTo(mapRef.current);
      stateLayerRef.current = stateLayer;
      mapRef.current.fitBounds(stateLayer.getBounds());
    }
  }, [stateGeoJson]);

  // Handle city layer and zoning grid
  useEffect(() => {
    if (cityLayerRef.current) {
      mapRef.current?.removeLayer(cityLayerRef.current);
    }
    if (gridLayerRef.current) { // Clear grid when city or zoning changes
      mapRef.current?.removeLayer(gridLayerRef.current);
      gridLayerRef.current = null;
    }

    if (cityGeoJson && mapRef.current) {
      const cityLayer = L.geoJSON(cityGeoJson, {
        style: {
          color: 'red',
        },
      }).addTo(mapRef.current);
      cityLayerRef.current = cityLayer;
      mapRef.current.fitBounds(cityLayer.getBounds());

      // Draw zoning grid if zoning value is provided and valid
      if (zoning > 0 && cityGeoJson.features && cityGeoJson.features.length > 0) {
        try {
          const cityFeature = cityGeoJson.features[0]; // Assuming the first feature is the main city polygon

          // Basic validation for cityFeature
          if (!cityFeature || !cityFeature.geometry) {
            console.error("Invalid city GeoJSON feature or geometry:", cityFeature);
            return;
          }

          const boundingBox = bbox(cityGeoJson); // Use named import
          const cellSide = Math.sqrt(zoning); // Side length of the square in meters

          // Create a square grid
          const grid = squareGrid(boundingBox, cellSide, { units: 'meters' }); // Use named import

          // Filter grid cells to only include those that intersect with the city polygon
          const intersectingCells = grid.features.filter(cell => {
            // Ensure both cell and cityFeature are valid GeoJSON objects for intersection
            if (!cell || !cell.geometry || !cityFeature.geometry) {
              console.warn("Skipping intersection check due to invalid geometry:", cell, cityFeature);
              return false;
            }
            return booleanIntersects(cell, cityFeature); // Use named import
          });

          const getPolygonColor = (index: number) => {
            if (index <= 1) {
              return '#90EE90'; // Light green
            } else if (index <= 3) {
              return 'yellow';
            } else if (index <= 5) {
              return 'orange';
            } else {
              return 'red';
            }
          };

          const gridLayer = L.geoJSON({
            type: 'FeatureCollection',
            features: intersectingCells
          } as GeoJSON.FeatureCollection, {
            style: (feature) => {
              if (feature && feature.properties && feature.properties.infestationIndex === undefined) {
                feature.properties.infestationIndex = 0; // Default value
              }
              return {
                fillOpacity: 0.2,
                color: getPolygonColor(feature?.properties?.infestationIndex || 0),
                weight: 1
              };
            },
            onEachFeature: (feature, layer) => {


              const updatePolygonColor = (newIndex: number) => {
                feature.properties.infestationIndex = newIndex;
                (layer as L.Path).setStyle({
                  color: getPolygonColor(newIndex)
                });
              };

              const popupContent = `
                <div>
                  <h4>Índice de Infestação Predial</h4>
                  <input type="number" id="infestationIndexInput" value="${feature.properties.infestationIndex}" min="0" step="0.1" />
                  <button id="saveInfestationIndex">Salvar</button>
                </div>
              `;

              layer.bindPopup(popupContent);

              layer.on('popupopen', () => {
                const input = document.getElementById('infestationIndexInput') as HTMLInputElement;
                const saveButton = document.getElementById('saveInfestationIndex') as HTMLButtonElement;

                if (input && saveButton) {
                  input.value = feature.properties.infestationIndex.toString();

                  saveButton.onclick = () => {
                    const newIndex = parseFloat(input.value);
                    if (!isNaN(newIndex) && newIndex >= 0) {
                      updatePolygonColor(newIndex);
                      layer.closePopup();
                    } else {
                      alert('Por favor, insira um valor numérico válido para o índice.');
                    }
                  };
                }
              });
            }
          }).addTo(mapRef.current);
          gridLayerRef.current = gridLayer;

        } catch (error) {
          console.error("Error generating zoning grid:", error);
        }
      }
    }
  }, [cityGeoJson, zoning]); // Add zoning to dependencies

  // Add legend control
  useEffect(() => {
    if (!mapRef.current) return;

    let legend: L.Control | null = null;

    if (zoning > 0) {
      legend = (L.control as any)({ position: 'topright' });

      legend!.onAdd = () => {
        const div = L.DomUtil.create('div', 'info legend');
        div.style.backgroundColor = 'white';
        div.style.padding = '10px';
        div.style.borderRadius = '5px';
        div.innerHTML =
          '<h4 style="margin-top: -5px;">Índice de Infestação Predial</h4>' +
          '<i style="background:#90EE90"></i> 0 - 1%<br>' +
          '<i style="background:yellow"></i> 1 - 3%<br>' +
          '<i style="background:orange"></i> 3 - 5%<br>' +
          '<i style="background:red"></i> > 5%<br>';
        return div;
      };
      legend!.addTo(mapRef.current);
    }

    return () => {
      if (legend) {
        mapRef.current?.removeControl(legend);
      }
    };
  }, [zoning]);

  return <div id="map" style={{ height: '100vh', width: '100%' }} />;
}

export default Map;
